import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { Session } from "@supabase/supabase-js";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { nivelDeSesion, supabase } from "@/lib/supabase";
import type { MemberRow } from "@/types/db";
import { api } from "@/lib/api";
import { useDatos } from "@/features/datos/store/store";
import { borrarLocal } from "@/features/datos/persistencia/local";

export interface EstadoMiembro { access_open: boolean; privacy_version: string | null; usage: { pct: number; warn: boolean; cap_reached: boolean }; ip_hash: string | null }

type Ctx = {
  /** undefined = cargando sesión */
  session: Session | null | undefined;
  /** undefined = cargando; null = sin fila en members (no está en la lista) */
  member: MemberRow | null | undefined;
  memberError: string | null;
  privacyVersion: string | null;
  needsConsent: boolean;
  /** Consentimiento expreso y por escrito para datos sensibles (Mi identidad, Diagnóstico, Rueda 360). */
  sensibles: boolean;
  esAdmin: boolean;
  /** Admin (o quien ya activó la verificación en dos pasos) con sesión que todavía no la pasó: ve SegundoFactor. */
  falta2fa: boolean;
  /** /api/me/status: acceso abierto, uso del mes (%), IP seudónima. undefined = cargando. */
  estado: EstadoMiembro | undefined;
  recargar: () => Promise<void>;
  salir: () => Promise<void>;
};
const AuthCtx = createContext<Ctx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null | undefined>(undefined);
  const qc = useQueryClient();

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setSession(data.session));
    const { data: sub } = supabase.auth.onAuthStateChange((evento, s) => {
      setSession(s);
      // Al salir no queda nada del miembro en memoria: en una compu compartida el siguiente no hereda "Mis datos" (revisión de Diego #7)
      if (evento === "SIGNED_OUT") { qc.clear(); useDatos.getState().reset(); }
      if (evento === "TOKEN_REFRESHED" || evento === "MFA_CHALLENGE_VERIFIED") qc.invalidateQueries();
    });
    return () => sub.subscription.unsubscribe();
  }, [qc]);

  const uid = session?.user.id;
  const memberQ = useQuery({
    queryKey: ["member", uid],
    enabled: !!uid,
    queryFn: async (): Promise<{ member: MemberRow | null; privacyVersion: string | null; sensibles: boolean }> => {
      const [m, s] = await Promise.all([
        supabase.from("members").select("*").eq("user_id", uid!).maybeSingle(),
        supabase.from("app_settings").select("value").eq("key", "privacy_version").maybeSingle(),
      ]);
      if (m.error) throw m.error;
      const pv = (s.data?.value as string | undefined) ?? null;
      if (m.data) void supabase.rpc("touch_last_seen");
      // La fila más reciente de "sensibles:*" / "sensibles-revocado:*" en consents dice si hay autorización vigente
      const sens = m.data ? await supabase.from("consents").select("version").eq("member_id", (m.data as MemberRow).id).like("version", "sensibles%").order("accepted_at", { ascending: false }).limit(1).maybeSingle() : null;
      return { member: (m.data as MemberRow | null) ?? null, privacyVersion: pv, sensibles: !!sens?.data?.version.startsWith("sensibles:") };
    },
  });

  const tieneMiembro = !!memberQ.data?.member;
  const estadoQ = useQuery({
    queryKey: ["member", uid, "status"],
    enabled: tieneMiembro,
    queryFn: () => api<EstadoMiembro>("/api/me/status"),
    staleTime: 60_000,
  });

  const valor = useMemo<Ctx>(() => {
    const member = session === undefined ? undefined : !session ? null : memberQ.isLoading || memberQ.isError ? undefined : (memberQ.data?.member ?? null);
    const privacyVersion = memberQ.data?.privacyVersion ?? null;
    const conFactor = (session?.user.factors ?? []).some((f) => f.status === "verified");
    return {
      session,
      member,
      memberError: memberQ.isError ? "No pude cargar tu cuenta. Revisa tu conexión." : null,
      privacyVersion,
      needsConsent: !!member && (member.consent_version === null || (privacyVersion !== null && member.consent_version !== privacyVersion)),
      sensibles: memberQ.data?.sensibles ?? false,
      esAdmin: member?.role === "admin",
      falta2fa: !!member && !!session && (member.role === "admin" || conFactor) && nivelDeSesion(session.access_token) !== "aal2",
      estado: estadoQ.data,
      recargar: async () => { await qc.invalidateQueries({ queryKey: ["member"] }); },
      salir: async () => { if (memberQ.data?.member) borrarLocal(memberQ.data.member.id); await supabase.auth.signOut(); },
    };
  }, [session, memberQ.data, memberQ.isLoading, memberQ.isError, estadoQ.data, qc]);

  return <AuthCtx.Provider value={valor}>{children}</AuthCtx.Provider>;
}

export function useAuth(): Ctx {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth fuera de AuthProvider");
  return ctx;
}

/** Igual que useAuth pero devuelve null fuera del provider (modo ejemplo, sin sesión). */
export function useAuthOptional(): Ctx | null {
  return useContext(AuthCtx);
}
